import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Calendar, Clock, User, Mail, Phone, Link2, RefreshCw, Loader2, AlertCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Format ISO datetime to readable
const fmtDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AcuityAppointmentsPanel({ eventId, rsvps = [], onLinked, onConnect }) {
  const [loading, setLoading] = useState(false);
  const [appointments, setAppointments] = useState([]);
  const [connected, setConnected] = useState(true);
  const [error, setError] = useState(null);
  const [linkingId, setLinkingId] = useState(null);

  const fetchAppointments = async () => {
    if (!eventId) return;
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/events/${eventId}/acuity-appointments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        setError("Failed to fetch appointments");
        return;
      }
      const data = await res.json();
      setAppointments(data.appointments || []);
      setConnected(data.connected !== false);
      if (data.error) setError(data.error);
    } catch (e) {
      setError(e.message || "Network error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAppointments();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const linkAppointment = async (appt, rsvpId) => {
    setLinkingId(appt.appointment_id);
    try {
      const token = localStorage.getItem("token") || localStorage.getItem("session_token");
      const res = await fetch(`${API}/host/events/${eventId}/link-acuity-appointment`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify({ appointment_id: appt.appointment_id, rsvp_id: rsvpId }),
      });
      if (res.ok) {
        toast.success("Appointment linked to client");
        onLinked && onLinked();
        fetchAppointments();
      } else {
        toast.error("Failed to link");
      }
    } catch {
      toast.error("Network error");
    } finally {
      setLinkingId(null);
    }
  };

  if (!connected) {
    return (
      <Card data-testid="acuity-not-connected">
        <CardContent className="py-12 text-center">
          <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
          <p className="text-sm text-gray-600 mb-4">Acuity is not connected for this dashboard.</p>
          <Button onClick={onConnect} data-testid="connect-acuity-cta">
            Connect Acuity
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card data-testid="acuity-appointments-panel">
      <CardHeader className="flex-row items-center justify-between">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="w-5 h-5 text-purple-600" />
          Acuity Appointments
          {appointments.length > 0 && (
            <Badge variant="secondary">{appointments.length}</Badge>
          )}
        </CardTitle>
        <Button size="sm" variant="outline" onClick={fetchAppointments} disabled={loading} data-testid="acuity-refresh-btn">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          <span className="ml-1.5 text-xs">Refresh</span>
        </Button>
      </CardHeader>
      <CardContent>
        {error && (
          <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-100 p-3 rounded-lg mb-4">
            <AlertCircle className="w-4 h-4" />
            {error}
          </div>
        )}

        {loading && appointments.length === 0 ? (
          <div className="py-12 text-center text-gray-400">
            <Loader2 className="w-6 h-6 mx-auto animate-spin mb-2" />
            Loading appointments...
          </div>
        ) : appointments.length === 0 ? (
          <div className="py-12 text-center text-gray-500 text-sm">
            No upcoming appointments found in Acuity.
          </div>
        ) : (
          <div className="space-y-2">
            {appointments.map((appt) => (
              <div
                key={appt.appointment_id}
                className="border border-gray-200 rounded-lg p-3 hover:border-purple-300 transition-colors"
                data-testid={`appointment-${appt.appointment_id}`}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <User className="w-4 h-4 text-gray-400" />
                      <span className="font-medium text-sm">
                        {appt.first_name} {appt.last_name}
                      </span>
                      {appt.canceled && (
                        <Badge variant="destructive" className="text-[10px]">Canceled</Badge>
                      )}
                      {appt.matched_rsvp_id && (
                        <Badge className="text-[10px] bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          Linked
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-500 flex-wrap">
                      {appt.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-3 h-3" /> {appt.email}
                        </span>
                      )}
                      {appt.phone && (
                        <span className="flex items-center gap-1">
                          <Phone className="w-3 h-3" /> {appt.phone}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {fmtDate(appt.datetime)}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">{appt.type}</p>
                  </div>

                  {!appt.matched_rsvp_id && (
                    <div className="flex items-center gap-2">
                      <Select
                        onValueChange={(v) => v && linkAppointment(appt, v)}
                        disabled={linkingId === appt.appointment_id || rsvps.length === 0}
                      >
                        <SelectTrigger
                          className="h-8 text-xs max-w-[180px]"
                          data-testid={`link-rsvp-select-${appt.appointment_id}`}
                        >
                          <SelectValue placeholder={rsvps.length === 0 ? "No clients yet" : "Link to client..."} />
                        </SelectTrigger>
                        <SelectContent>
                          {rsvps.map((r) => (
                            <SelectItem key={r.rsvp_id} value={r.rsvp_id}>
                              {r.name || r.email}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {linkingId === appt.appointment_id && (
                        <Loader2 className="w-3 h-3 animate-spin text-purple-600" />
                      )}
                    </div>
                  )}
                  {appt.matched_rsvp_id && (
                    <div className="flex items-center gap-1 text-xs text-emerald-700">
                      <Link2 className="w-3 h-3" />
                      <span>{appt.matched_name}</span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
